"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const locations = [{
  id: 1,
  name: "Miami Metro",
  address: "33131 | 25.7617° N, 80.1918° W",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/miami-skyline.png",
  link: "/home-inspection-miami-fl/"
}, {
  id: 2,
  name: "The Keys",
  address: "33037 | 25.0865° N, 80.4473° W",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/keys-waterfront.png",
  link: "/home-inspection-florida-keys/"
}, {
  id: 3,
  name: "Palm Beach",
  address: "33401 | 26.7153° N, 80.0534° W",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/palm-beach.png",
  link: "#"
}];
export default function OurLocations() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="85225" id="service-areas" className="bg-background text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-5xl font-extrabold tracking-tighter text-secondary mb-6 font-sans">
            Our Regional Commitment
          </h2>
          <Separator className="mb-12 bg-primary h-px w-full opacity-30" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {locations.map((loc, i) => <LocationCard key={loc.id} location={loc} index={i} />)}
        </div>
      </div>
    </section>;
}
function LocationCard({
  location,
  index
}: {
  location: (typeof locations)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out h-full ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full overflow-hidden border-border/40 shadow-sm hover:shadow-xl transition-all duration-500 bg-card flex flex-col rounded-lg">
        <div className="relative h-64 w-full shrink-0 group">
          <img src={location.image} alt={location.name} className="object-cover w-full h-full grayscale hover:grayscale-0 transition-all duration-700" />
          <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-500 flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 font-mono text-primary font-bold text-sm tracking-widest transition-opacity duration-500">
              VIEW DISTRICT
            </span>
          </div>
        </div>
        <CardContent className="px-6 pb-6 pt-6 flex-grow flex flex-col items-start text-left">
          <h3 className="text-2xl font-bold text-secondary mb-3 tracking-tight font-sans">
            {location.name}
          </h3>
          <p className="text-muted-foreground mb-8 flex-grow text-xs font-mono uppercase tracking-wider">
            {location.address}
          </p>
          <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md h-12 text-sm font-bold tracking-wide shadow-md transition-all duration-300">
            <Link to={location.link}>
              <MapPin className="mr-2 h-4 w-4" />
              Get Directions
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>;
}